import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setEditingBoundary } from '../../../../../store/slices/projectItemSlice';

const BOUNDARY_SOURCE = 'boundary-source';
const BOUNDARY_FILL   = 'boundary-fill';
const BOUNDARY_LINE   = 'boundary-line';
const HANDLE_SOURCE   = 'boundary-handle-source';
const HANDLE_LAYER    = 'boundary-handle-layer';

const DEFAULT_METERS = 10;

export const boundaryRef = { moveTo: null, getCornersForPin: null };


function metersToLng(m, lat) { return m / (111320 * Math.cos((lat * Math.PI) / 180)); }
function metersToLat(m)      { return m / 111320; }

function orient(p, q, r) {
  return (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
}
function segmentsIntersect(p1, p2, p3, p4) {
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  return (((o1 > 0 && o2 < 0) || (o1 < 0 && o2 > 0)) &&
          ((o3 > 0 && o4 < 0) || (o3 < 0 && o4 > 0)));
}
function pointInPolygon(pt, poly) {
  let inside = false;
  const [x, y] = pt;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect = ((yi > y) !== (yj > y)) &&
      (x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-20) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}
function polygonsOverlap(a, b) {
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i];
    const a2 = a[(i + 1) % a.length];
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j];
      const b2 = b[(j + 1) % b.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  for (const pt of a) if (pointInPolygon(pt, b)) return true;
  for (const pt of b) if (pointInPolygon(pt, a)) return true;
  return false;
}
function polygonSelfIntersects(corners) {
  const n = corners.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a1 = corners[i];
    const a2 = corners[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i || j === (i + 1) % n || (j + 1) % n === i) continue;
      const b1 = corners[j];
      const b2 = corners[(j + 1) % n];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return false;
}
function wouldOverlapOthers(candidate, selfPinId, cornersMap) {
  if (!candidate || candidate.length < 3) return false;
  for (const pid of Object.keys(cornersMap)) {
    if (String(pid) === String(selfPinId)) continue;
    const other = cornersMap[pid];
    if (!other || other.length < 3) continue;
    if (polygonsOverlap(candidate, other)) return true;
  }
  return false;
}
function distToSegmentSq(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  let t = 0;
  if (len2 > 0) {
    t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
  }
  const cx = a[0] + t * dx;
  const cy = a[1] + t * dy;
  const ex = p[0] - cx;
  const ey = p[1] - cy;
  return ex * ex + ey * ey;
}
function closestEdgeIndex(corners, pt) {
  let bestIdx = 0;
  let best = Infinity;
  for (let i = 0; i < corners.length; i++) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const d = distToSegmentSq(pt, a, b);
    if (d < best) { best = d; bestIdx = i; }
  }
  return bestIdx;
}

function defaultCorners(cx, cy) {
  const dLng = metersToLng(DEFAULT_METERS, cy);
  const dLat = metersToLat(DEFAULT_METERS);
  return [
    [cx - dLng, cy + dLat],
    [cx + dLng, cy + dLat],
    [cx + dLng, cy - dLat],
    [cx - dLng, cy - dLat],
  ];
}
 

function safeInitialCorners(cx, cy, selfPinId, cornersMap) {
  let size = DEFAULT_METERS;
  while (size >= 1) {
    const dLng = metersToLng(size, cy);
    const dLat = metersToLat(size);
    const candidate = [
      [cx - dLng, cy + dLat],
      [cx + dLng, cy + dLat],
      [cx + dLng, cy - dLat],
      [cx - dLng, cy - dLat],
    ];
    if (!wouldOverlapOthers(candidate, selfPinId, cornersMap)) return candidate;
    size /= 2;
  }
  const dLng = metersToLng(1, cy);
  const dLat = metersToLat(1);
  return [
    [cx - dLng, cy + dLat],
    [cx + dLng, cy + dLat],
    [cx + dLng, cy - dLat],
    [cx - dLng, cy - dLat],
  ];
}

function parsePos(positions) {
  let pos = positions;
  if (typeof pos === 'string') {
    try { pos = JSON.parse(pos); } catch { return null; }
  }
  if (pos?.x == null || pos?.y == null) return null;
  return pos;
}

function parseCorners(raw, cx, cy) {
  if (!raw || raw === 'null') return null;
  let attrs = raw;
  if (typeof attrs === 'string') {
    try { attrs = JSON.parse(attrs); } catch { return null; }
    if (typeof attrs === 'string') {
      try { attrs = JSON.parse(attrs); } catch { return null; }
    }
  }
  if (Array.isArray(attrs?.corners) && attrs.corners.length >= 3) {
    return attrs.corners.map((c) => [...c]);
  }
  if (attrs?.offsets) {
    const { top, right, bottom, left } = attrs.offsets;
    return [
      [cx - metersToLng(left, cy),  cy + metersToLat(top)],
      [cx + metersToLng(right, cy), cy + metersToLat(top)],
      [cx + metersToLng(right, cy), cy - metersToLat(bottom)],
      [cx - metersToLng(left, cy),  cy - metersToLat(bottom)],
    ];
  }
  return null;
}

function syncSources(map, cornersMap, pinMap, editPinId, isEdit) {
  if (!map) return;
  const polyFeatures   = [];
  const handleFeatures = [];
  
  for (const [encId, corners] of Object.entries(cornersMap)) {
    const pinData = pinMap[encId];
    if (!pinData) continue;
    const color  = pinData.boundary_color || '#26A3DB';
    const closed = [...corners, corners[0]];
    
    polyFeatures.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [closed] },
      properties: { color, pinId: String(encId) },
    });
    
    if (isEdit && String(encId) === String(editPinId)) {
      corners.forEach((coord, i) => {
        handleFeatures.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: coord },
          properties: { index: i, enc_id: encId },
        });
      });
    }
  }
  
  try { map.getSource(BOUNDARY_SOURCE)?.setData({ type: 'FeatureCollection', features: polyFeatures }); } catch {}
  try { map.getSource(HANDLE_SOURCE)?.setData({ type: 'FeatureCollection', features: handleFeatures }); } catch {}
}

 

const BoundaryLayer = () => {
  const map              = useSelector((s) => s.map.mapContainer);
  const allPins          = useSelector((s) => s.api.allPins);
  const currentFloor     = useSelector((s) => s.api.currentFloor);
  const editingPinId     = useSelector((s) => s.api.editingPinId);
  const editingBoundary  = useSelector((s) => s.api.editingBoundary);
  const isEditPage       = !!useMatch('/project/:id/location/:subid');
  
  const dispatch = useDispatch();
  
  const dragging          = useRef({ active: false, cornerIdx: null, pinId: null });
  const cornersMapRef     = useRef({});
  const pinMapRef         = useRef({});
  const editPinIdRef      = useRef(editingPinId);
  const isEditRef         = useRef(isEditPage);
  const lastHandleClickRef   = useRef(null);
  const suppressDblClickRef  = useRef(false); 
  const lastOverlapToastRef  = useRef(0);
  
  editPinIdRef.current = editingPinId;
  isEditRef.current    = isEditPage; 
  
  useEffect(() => {
    if (!map) return;
    const floorId = currentFloor?.enc_id;
    if (!floorId) return;

    const setup = () => {
      const locations     = allPins?.location || [];
      const newCornersMap = {};
      const newPinMap     = {};

      for (const pin of locations) {
        if (pin.fp_id !== floorId || !pin.positions) continue; 
        if (isEditPage && editingPinId && String(pin.enc_id) === String(editingPinId)) continue;
        if (!pin.boundary_attributes || pin.boundary_attributes === 'null') continue;

        const pos = parsePos(pin.positions);
        if (!pos) continue;
        const corners = parseCorners(pin.boundary_attributes, pos.x, pos.y);
        if (!corners) continue;

        newCornersMap[pin.enc_id] = corners;
        newPinMap[pin.enc_id]     = { pos, boundary_color: pin.boundary_color || '#26A3DB' };
      }

      if (isEditPage && editingBoundary && editingPinId) {
        const pin = locations.find((p) => String(p.enc_id) === String(editingPinId) && p.fp_id === floorId);
        if (pin?.positions) {
          const pos = parsePos(pin.positions);
          if (pos) {
            const corners = parseCorners(editingBoundary.attributes, pos.x, pos.y)
              || safeInitialCorners(pos.x, pos.y, pin.enc_id, newCornersMap);
            newCornersMap[pin.enc_id] = corners;
            newPinMap[pin.enc_id]     = { pos, boundary_color: editingBoundary.color || '#26A3DB' };

          }
        }
      }

      cornersMapRef.current = newCornersMap;
      pinMapRef.current     = newPinMap;

      const emptyFC = { type: 'FeatureCollection', features: [] };

      if (!map.getSource(BOUNDARY_SOURCE)) {
        map.addSource(BOUNDARY_SOURCE, { type: 'geojson', data: emptyFC });
        map.addLayer({ id: BOUNDARY_FILL, type: 'fill', source: BOUNDARY_SOURCE,
          paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.25 }
        });
        map.addLayer({ id: BOUNDARY_LINE, type: 'line', source: BOUNDARY_SOURCE,
          paint: { 'line-color': ['get', 'color'], 'line-width': 2 }
        });
      }

      if (!map.getSource(HANDLE_SOURCE)) {
        map.addSource(HANDLE_SOURCE, { type: 'geojson', data: emptyFC });
        map.addLayer({ id: HANDLE_LAYER, type: 'circle', source: HANDLE_SOURCE,
          paint: { 'circle-radius': 6, 'circle-color': '#5B21B6', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 }
        });
      }

      syncSources(map, newCornersMap, newPinMap, editingPinId, isEditPage);

      boundaryRef.moveTo = (pinId, lng, lat) => {
        const key        = String(pinId);
        const pinData    = pinMapRef.current[key];
        const oldCorners = cornersMapRef.current[key];
        if (!pinData || !oldCorners) return { ok: true, lng, lat };
        const dx = lng - pinData.pos.x;
        const dy = lat - pinData.pos.y;
        const candidate = oldCorners.map(([cx, cy]) => [cx + dx, cy + dy]);
        if (wouldOverlapOthers(candidate, key, cornersMapRef.current)) {
          return { ok: false, lng: pinData.pos.x, lat: pinData.pos.y };
        }
        cornersMapRef.current[key] = candidate;
        pinMapRef.current[key]     = { ...pinData, pos: { x: lng, y: lat } };
        syncSources(map, cornersMapRef.current, pinMapRef.current, editPinIdRef.current, isEditRef.current);
        return { ok: true, lng, lat };
      };

      boundaryRef.getCornersForPin = (pinId) => {
        const corners = cornersMapRef.current[String(pinId)];
        return corners ? corners.map(([cx, cy]) => [cx, cy]) : null;
      };

 
      boundaryRef.wouldNewBoundaryOverlap = (pinId, cx, cy) => {
        const key       = String(pinId);
        const candidate = safeInitialCorners(cx, cy, key, cornersMapRef.current);
        return wouldOverlapOthers(candidate, key, cornersMapRef.current);
      };
    };

    if (map.isStyleLoaded()) {
      setup();
    } else { 
      map.once('style.load', setup);
 
      const onStyleData = () => {
        if (map.isStyleLoaded()) {
          setup();
          map.off('styledata', onStyleData);  
        }
      };
      map.on('styledata', onStyleData);
    }

    return () => {
      boundaryRef.moveTo           = null;
      boundaryRef.getCornersForPin = null;
      map.off('style.load', setup);
      try {
        [HANDLE_LAYER, BOUNDARY_LINE, BOUNDARY_FILL].forEach((l) => { if (map.getLayer(l))   map.removeLayer(l); });
        [BOUNDARY_SOURCE, HANDLE_SOURCE].forEach((s)             => { if (map.getSource(s)) map.removeSource(s); });
      } catch {}
    };
  }, [map, allPins, currentFloor, isEditPage, editingBoundary, editingPinId]);


  useEffect(() => {
    if (!map || !isEditPage) return;
    
    const onMouseDown = (e) => {
      if (!map.getLayer(HANDLE_LAYER)) return;
      const features = map.queryRenderedFeatures(e.point, { layers: [HANDLE_LAYER] });
      if (!features.length) return;
      e.preventDefault();

      const idx   = features[0].properties.index;
      const pinId = features[0].properties.enc_id;

      const now  = Date.now();
      const last = lastHandleClickRef.current;
      if (
        isEditRef.current &&
        last &&
        now - last.time < 400 &&
        last.idx === idx &&
        String(last.pinId) === String(pinId)
      ) {
        lastHandleClickRef.current = null;

        const corners = cornersMapRef.current[pinId];
        if (corners && corners.length > 4) {
          const newCorners = corners.filter((_, i) => i !== idx);
          if (!wouldOverlapOthers(newCorners, pinId, cornersMapRef.current)) {
            cornersMapRef.current[pinId] = newCorners;
            syncSources(map, cornersMapRef.current, pinMapRef.current, editPinIdRef.current, isEditRef.current);
            const pinData = pinMapRef.current[pinId];
            dispatch(setEditingBoundary({
              color: pinData?.boundary_color || '#26A3DB',
              attributes: JSON.stringify({ corners: newCorners }),
            }));
          }
        }
        
        suppressDblClickRef.current = true;
        return;
      }

      lastHandleClickRef.current = { time: now, idx, pinId };

      dragging.current = { active: true, cornerIdx: idx, pinId };
      map.dragPan.disable();
      map.getCanvas().style.cursor = 'grabbing';
    };
    
    const warnOverlap = () => {
      const now = Date.now();
      if (now - lastOverlapToastRef.current < 1500) return;
      lastOverlapToastRef.current = now;
      toast.warning('Boundary overlaps another location boundary.', { toastId: 'boundary-overlap' });
    };

    const onMouseMove = (e) => {
      if (!dragging.current.active) return;
      const { lng, lat } = e.lngLat;
      const { cornerIdx, pinId } = dragging.current;
      const corners = cornersMapRef.current[pinId];
      if (!corners || corners.length < 3) return;

      const candidate = corners.map((c, i) => (i === cornerIdx ? [lng, lat] : [...c]));
 
      const pinData = pinMapRef.current[pinId];
      if (pinData && !pointInPolygon([pinData.pos.x, pinData.pos.y], candidate)) {
        return;
      }

      if (polygonSelfIntersects(candidate)) {
        return;
      }

      if (wouldOverlapOthers(candidate, pinId, cornersMapRef.current)) {
        warnOverlap();
        return;
      }
      cornersMapRef.current[pinId] = candidate;
      syncSources(map, cornersMapRef.current, pinMapRef.current, editPinIdRef.current, isEditRef.current);
    };
    
    const onMouseUp = () => {
      if (!dragging.current.active) return;
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      const { pinId } = dragging.current;
      dragging.current = { active: false, cornerIdx: null, pinId: null };
      
      const pinData = pinMapRef.current[pinId];
      const corners = cornersMapRef.current[pinId];
      if (!pinData || !corners) return;
      
      dispatch(setEditingBoundary({
        color: pinData.boundary_color || '#26A3DB',
        attributes: JSON.stringify({ corners }),
      }));
    };
    
    const onDblClick = (e) => {
      if (suppressDblClickRef.current) {
        suppressDblClickRef.current = false;
        e.preventDefault();
        return;
      }

      const editPinId = editPinIdRef.current;
      if (!editPinId) return;

      const layers = [];
      if (map.getLayer(BOUNDARY_LINE)) layers.push(BOUNDARY_LINE);
      if (map.getLayer(BOUNDARY_FILL)) layers.push(BOUNDARY_FILL);
      if (!layers.length) return;
      const features = map.queryRenderedFeatures(e.point, { layers });
      const feat = features.find((f) => String(f.properties?.pinId) === String(editPinId));
      if (!feat) return;
      e.preventDefault();

      const corners = cornersMapRef.current[editPinId];
      if (!corners || corners.length < 3) return;
      const { lng, lat } = e.lngLat;
      const edgeIdx = closestEdgeIndex(corners, [lng, lat]);
      const newCorners = [
        ...corners.slice(0, edgeIdx + 1),
        [lng, lat],
        ...corners.slice(edgeIdx + 1),
      ];

      if (polygonSelfIntersects(newCorners)) {
        return;
      }

      if (wouldOverlapOthers(newCorners, editPinId, cornersMapRef.current)) {
        warnOverlap();
        return;
      }

      const pinData = pinMapRef.current[editPinId];
      if (pinData && !pointInPolygon([pinData.pos.x, pinData.pos.y], newCorners)) return;

      cornersMapRef.current[editPinId] = newCorners;
      syncSources(map, cornersMapRef.current, pinMapRef.current, editPinIdRef.current, isEditRef.current);

      dispatch(setEditingBoundary({
        color: pinData?.boundary_color || '#26A3DB',
        attributes: JSON.stringify({ corners: newCorners }),
      }));
    };

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);
    map.on('dblclick', onDblClick);
    return () => {
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      map.off('dblclick', onDblClick);
    };
  }, [map, isEditPage, dispatch]);
  
  return null;
};

export default BoundaryLayer;
